import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';

interface SetContactsProps {
  type: 'endpoint' | 'service';
  id: string;
  primaryContact?: string;
  secondaryContact?: string;
  onSubmit: () => void;
}
const SetContacts = ({ type, id, primaryContact = '', secondaryContact = '', onSubmit }: SetContactsProps) => {
  const api = useApi();
  const { isLoading, data } = useQuery(
    ['getContacts'],
    api.getContactApiV1GetContactGet.bind(null, {}),
    { enabled: true },
  );
  const [businessContacts, setBusinessContacts] = useState<string>(primaryContact);
  const [technicalContacts, setTechnicalContacts] = useState<string>(secondaryContact);
  const handleBusinessChange = (event: SelectChangeEvent<typeof businessContacts>) => {
    setBusinessContacts(event.target.value);
  };
  const handleTechnicalChange = (event: SelectChangeEvent<typeof technicalContacts>) => {
    setTechnicalContacts(event.target.value);
  };

  const AppServiceContactPost = useMutation(api.appServiceUpdateContactApiV1SetAppServiceContactPost, {
    onSuccess() {
      toast.success('App service contact updated.');
      onSubmit();
    },
    onError: apiError,
  });

  const EndpointContactPost = useMutation(api.endpointUpdateContactApiV1SetEndpointContactPost, {
    onSuccess() {
      toast.success('App service contact updated.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (type === 'endpoint') {
      EndpointContactPost.mutate({
        ep_id: id,
        primary_contact_id: businessContacts,
        secondary_contact_id: technicalContacts,
      });
    }
    if (type === 'service') {
      AppServiceContactPost.mutate({
        app_service_id: id,
        primary_contact_id: businessContacts,
        secondary_contact_id: technicalContacts,
      });
    }
  };

  if (isLoading) return <Loading />;
  if (data?.data.length === 0) {
    return (
      <Alert severity="error">
        <AlertTitle>Missing Contacts</AlertTitle>
        There are no contacts available. Please <Link component={RouterLink} color="inherit" to="/contacts">create a contact</Link> first.
      </Alert>
    );
  }
  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 5 }}>
        <InputLabel id="business-contact-label">Business Contact</InputLabel>
        <Select
          labelId="business-contact-label"
          id="business-contact"
          value={businessContacts}
          label="Business Contact"
          onChange={handleBusinessChange}
        >
          <MenuItem disabled value="">
            <em>Choose a contact</em>
          </MenuItem>
          {data?.data.map(({ first_name, last_name, email, contact_id }) => (
            <MenuItem
              key={contact_id}
              value={contact_id}
            >
              {first_name} {last_name} ({email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 5 }}>
        <InputLabel id="technical-contact-label">Technical Contact</InputLabel>
        <Select
          labelId="technical-contact-label"
          id="technical-contact"
          value={technicalContacts}
          label="Technical Contact"
          onChange={handleTechnicalChange}
        >
          <MenuItem disabled value="">
            <em>Choose a contact</em>
          </MenuItem>
          {data?.data.map(({ first_name, last_name, email, contact_id }) => (
            <MenuItem
              key={contact_id}
              value={contact_id}
            >
              {first_name} {last_name} ({email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit}>
          Save Contacts
        </Button>
      </Box>
    </Box>
  );
};

export default SetContacts;